import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, ChevronDown, PhoneCall } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const dropdowns = {
    about: {
      title: "About RDS",
      items: [
        { name: "Vision & Mission", link: "/vision" },
        { name: "University", link: "/university" },
        { name: "Principal's Message", link: "/principal-message" },
        { name: "Chairman's Message", link: "/chairman" },
      ],
      mainLink: null, 
    },
    courses: {
      title: "Courses",
      items: [
        { name: "B. Pharma", link: "/bpharma" },
        { name: "D. Pharma", link: "/dpharma" },
        { name: "Fee Structure", link: "/fee-structure" },
      ],
      mainLink: null,
    },
    admission: {
      title: "Admission",
      items: [
        { name: "Registration Form", link: "/resistration" },
        { name: "Prospectus", link: "/prospective" },
        { name: "RDS Affiliation", link: "/affiliation" },
        { name: "Pharmacy Schedule", link: "/pharmacy" },
        { name: "Committee", link: "/committee" },
      ],
      mainLink: "/admission", // overview page for admission
    }
  };

  const mainNavItems = [
    { name: "Home", link: "/", hasDropdown: false },
    { name: dropdowns.about.title, link: "#", hasDropdown: true, dropdownKey: "about", mainLink: dropdowns.about.mainLink },
    { name: dropdowns.courses.title, link: "#", hasDropdown: true, dropdownKey: "courses", mainLink: dropdowns.courses.mainLink },
    { name: dropdowns.admission.title, link: "#", hasDropdown: true, dropdownKey: "admission", mainLink: dropdowns.admission.mainLink },
    { name: "Placement", link: "/placement", hasDropdown: false },
    { name: "Gallery", link: "/gallery", hasDropdown: false },
    { name: "Contact", link: "/contact", hasDropdown: false },
  ];

  const handleDropdownToggle = (key) => {
    setOpenDropdown(openDropdown === key ? null : key);
  };
  
  const handleMobileLinkClick = () => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  return (
    <>
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white py-2.5 px-4 w-full overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="hidden md:flex justify-between items-center">
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2 bg-white/20 px-4 py-1.5 rounded-full">
                <PhoneCall size={16} className="animate-pulse" />
                <span className="font-semibold text-sm">ANTI RAGGING TOLL FREE:</span>
                <a href="tel:18001805522" className="hover:underline font-mono text-sm font-bold">1800 180 5522</a>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={14} />
                <a href="tel:+919956808612" className="hover:underline text-sm">+91 9956808612</a>
                <span className="text-white/50">|</span>
                <a href="tel:+919628362187" className="hover:underline text-sm">+91 9628362187</a>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Mail size={14} />
              <a href="mailto:rdspharmacycollege@gmail.com" className="hover:underline text-sm truncate max-w-[250px]">
                rdspharmacycollege@gmail.com
              </a>
            </div>
          </div>
          <div className="md:hidden">
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center justify-center gap-2 bg-white/20 px-3 py-1.5 rounded-full w-full max-w-[280px] mx-auto">
                <PhoneCall size={14} className="animate-pulse" />
                <span className="font-semibold text-xs">ANTI RAGGING:</span>
                <a href="tel:18001805522" className="hover:underline font-mono text-xs font-bold">1800 180 5522</a>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3 text-xs">
                <a href="tel:+919956808612" className="hover:underline">+91 9956808612</a>
                <span className="text-white/50">•</span>
                <a href="tel:+919628362187" className="hover:underline">+91 9628362187</a>
                <span className="text-white/50">•</span>
                <a href="mailto:rdspharmacycollege@gmail.com" className="hover:underline truncate max-w-[180px]">
                  rdspharmacycollege@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <nav className={`bg-white shadow-lg transition-all duration-300 sticky top-0 z-50 ${isScrolled ? 'shadow-xl' : ''}`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center py-2 gap-3">
              <Link to="/">
                <img 
                  src="/logo.png" 
                  alt="RDS Pharmacy College Logo" 
                  className="h-18 md:h-14 w-auto object-contain"
                  onError={(e) => { 
                    e.target.onerror = null; 
                    e.target.src = 'https://via.placeholder.com/60x60?text=RDS';
                  }}
                />
              </Link>
            </div>
            <div className="hidden lg:flex items-center space-x-1">
              {mainNavItems.map((item) => (
                <div key={item.name} className="relative group">
                  {item.hasDropdown ? (
                    <div className="flex items-center">
                      {/* Main link (if exists) */}
                      {item.mainLink ? (
                        <Link
                          to={item.mainLink}
                          className="px-3 py-6 text-gray-700 font-medium hover:text-sky-600 whitespace-nowrap"
                        >
                          {item.name}
                        </Link>
                      ) : (
                        <span className="px-3 py-6 text-gray-700 font-medium whitespace-nowrap">
                          {item.name}
                        </span>
                      )}
                      <button
                        onClick={() => handleDropdownToggle(item.dropdownKey)}
                        className="p-2 text-gray-500 hover:text-sky-600 focus:outline-none"
                      >
                        <ChevronDown size={16} className={`transition-transform duration-300 ${openDropdown === item.dropdownKey ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                  ) : (
                    <Link
                      to={item.link}
                      className="relative px-3 py-6 text-gray-700 font-medium hover:text-sky-600 whitespace-nowrap"
                    >
                      {item.name}
                    </Link>
                  )}
                  {item.hasDropdown && openDropdown === item.dropdownKey && (
                    <div className="absolute top-full left-0 w-64 bg-white shadow-xl rounded-b-lg z-50 border-t-2 border-sky-500">
                      {dropdowns[item.dropdownKey].items.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.link}
                          onClick={() => setOpenDropdown(null)}
                          className="block px-4 py-3 text-gray-700 hover:bg-sky-50 hover:text-sky-600 border-b border-gray-100 last:border-0"
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-md text-gray-700 hover:text-sky-600 hover:bg-sky-50"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
          {isMobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-200">
              {mainNavItems.map((item) => (
                <div key={item.name} className="border-b border-gray-100">
                  {item.hasDropdown ? (
                    <div>
                      <div className="flex items-center justify-between">
                        {item.mainLink ? (
                          <Link
                            to={item.mainLink}
                            onClick={handleMobileLinkClick}
                            className="py-3 text-gray-700 font-medium"
                          >
                            {item.name}
                          </Link>
                        ) : (
                          <span className="py-3 text-gray-700 font-medium">
                            {item.name}
                          </span>
                        )}
                        <button
                          onClick={() => handleDropdownToggle(item.dropdownKey)}
                          className="p-2 text-gray-500"
                        >
                          <ChevronDown size={18} className={`transition-transform duration-300 ${openDropdown === item.dropdownKey ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                      {openDropdown === item.dropdownKey && (
                        <div className="pl-4 pb-3 space-y-2">
                          {dropdowns[item.dropdownKey].items.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.link}
                              onClick={handleMobileLinkClick}
                              className="block py-2 text-gray-600 hover:text-sky-600 text-sm"
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.link}
                      onClick={handleMobileLinkClick}
                      className="block py-3 text-gray-700 font-medium"
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                <div className="bg-red-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-700 font-semibold text-sm mb-2">
                    <PhoneCall size={14} />
                    <span>Anti Ragging Toll Free</span>
                  </div>
                  <a href="tel:18001805522" className="text-red-600 font-mono text-sm">1800 180 5522</a>
                </div>
                <div className="space-y-2">
                  <a href="tel:+919956808612" className="flex items-center gap-2 text-gray-600 text-sm">
                    <Phone size={12} />
                    +91 9956808612
                  </a>
                  <a href="tel:+919628362187" className="flex items-center gap-2 text-gray-600 text-sm">
                    <Phone size={12} />
                    +91 9628362187
                  </a>
                  <a href="mailto:rdspharmacycollege@gmail.com" className="flex items-center gap-2 text-gray-600 text-sm break-all">
                    <Mail size={12} />
                    rdspharmacycollege@gmail.com
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Navbar;